import { create } from 'zustand';
import type { Thread, Message, AppUser, SenderIdentity, ContactSummary, SenderIdentitySetting, PendingAction } from '../lib/api';
import { threadsApi, messagesApi, usersApi, contactsApi, settingsApi } from '../lib/api';
import { getSocket } from '../lib/socket';

interface TypingUser { userId: string; name: string; }

interface ChatState {
  threads: Thread[];
  activeThreadId: string | null;
  messages: Record<string, Message[]>;
  loadingMessages: boolean;
  users: AppUser[];
  onlineUserIds: Set<string>;
  typingUsers: Record<string, TypingUser[]>;
  /** threadId → { by: string } — threads this session user has left or been removed from */
  leftThreads: Record<string, { removedBy?: string }>;
  /** Workspace-wide "sign outbound messages" default (settings.sender_identity.enabled). null until loaded. */
  workspaceSignDefault: boolean | null;

  loadThreads: () => Promise<void>;
  loadWorkspaceSettings: () => Promise<void>;
  selectThread: (id: string) => Promise<void>;
  sendMessage: (content: string, isInternal?: boolean, media?: { mediaUrl: string; mediaFilename: string; messageType: 'image' | 'document' }) => Promise<void>;
  leaveThread: (threadId: string) => Promise<void>;
  setSenderIdentity: (threadId: string, value: SenderIdentity) => Promise<void>;
  updateContact: (threadId: string, contactId: string, patch: { display_name?: string | null; company?: string | null }) => Promise<ContactSummary>;
  startDm: (userId: string) => Promise<{ data: { id: string } }>;
  createGroup: (name: string, participantIds: string[]) => Promise<{ data: { id: string } }>;
  loadUsers: () => Promise<void>;
  setupSocketListeners: () => void;
  /** Re-join rooms and refetch threads + active messages after a (re)connect to reconcile missed events */
  resync: () => Promise<void>;
}

export const useChatStore = create<ChatState>((set, get) => ({
  threads: [],
  activeThreadId: null,
  messages: {},
  loadingMessages: false,
  users: [],
  onlineUserIds: new Set(),
  typingUsers: {},
  leftThreads: {},
  workspaceSignDefault: null,

  loadThreads: async () => {
    const { data } = await threadsApi.list();
    set({ threads: data });
  },

  loadWorkspaceSettings: async () => {
    try {
      const { data } = await settingsApi.getAll();
      const si = data.sender_identity as SenderIdentitySetting | undefined;
      set({ workspaceSignDefault: si?.enabled ?? false });
    } catch {
      set({ workspaceSignDefault: false });
    }
  },

  selectThread: async (id) => {
    set((s) => ({
      activeThreadId: id,
      loadingMessages: true,
      threads: s.threads.map((t) => t.id === id ? { ...t, unread_count: '0' } : t),
    }));
    const socket = getSocket();
    socket.emit('join_thread', id);

    try {
      if (!get().messages[id]) {
        const { data } = await messagesApi.list(id);
        set((s) => ({ messages: { ...s.messages, [id]: data }, loadingMessages: false }));
      } else {
        set({ loadingMessages: false });
      }
    } catch (err) {
      console.error('selectThread: failed to load messages', err);
      set((s) => ({ messages: { ...s.messages, [id]: s.messages[id] ?? [] }, loadingMessages: false }));
    }
  },

  sendMessage: async (content, isInternal = false, media) => {
    const { activeThreadId } = get();
    if (!activeThreadId) return;
    const { data } = await messagesApi.send(activeThreadId, {
      content,
      isInternal,
      ...(media && { messageType: media.messageType, mediaUrl: media.mediaUrl, mediaFilename: media.mediaFilename }),
    });
    // Fallback in case socket event is missed
    set((s) => {
      const existing = s.messages[activeThreadId] ?? [];
      if (existing.find((m) => m.id === data.id)) return s;
      return { messages: { ...s.messages, [activeThreadId]: [...existing, data] } };
    });
    set((s) => ({
      threads: s.threads.map((t) =>
        t.id === activeThreadId
          ? { ...t, last_message: content || media?.mediaFilename, last_message_at: data.created_at, last_message_sender: 'You' }
          : t
      ),
    }));
  },

  startDm: async (userId) => {
    // Find an existing DM thread with the target user
    const existingDm = get().threads.find((t) => {
      if (t.type !== 'direct') return false;
      const parts = t.participants ?? [];
      return parts.some((p) => p.user_id === userId);
    });
    if (existingDm) {
      return { data: { id: existingDm.id } };
    }
    const result = await threadsApi.create({ type: 'direct', dmUserId: userId });
    const { data: threads } = await threadsApi.list();
    set({ threads });
    return result;
  },

  createGroup: async (name, participantIds) => {
    const result = await threadsApi.create({ type: 'internal', name, participantIds });
    const { data: threads } = await threadsApi.list();
    set({ threads });
    return result;
  },

  setSenderIdentity: async (threadId, value) => {
    // Optimistic update; revert on failure
    const prev = get().threads.find((t) => t.id === threadId)?.sender_identity ?? 'default';
    set((s) => ({ threads: s.threads.map((t) => t.id === threadId ? { ...t, sender_identity: value } : t) }));
    try {
      await threadsApi.update(threadId, { sender_identity: value });
    } catch (err) {
      set((s) => ({ threads: s.threads.map((t) => t.id === threadId ? { ...t, sender_identity: prev } : t) }));
      throw err;
    }
  },

  updateContact: async (threadId, contactId, patch) => {
    const { data } = await contactsApi.update(contactId, patch);
    // Update the active thread immediately; other threads sharing this contact
    // refresh via the `thread:updated` socket event.
    set((s) => ({
      threads: s.threads.map((t) => t.id === threadId ? { ...t, contact: data } : t),
    }));
    return data;
  },

  leaveThread: async (threadId) => {
    const thread = get().threads.find((t) => t.id === threadId);
    if (thread?.type === 'org') {
      throw new Error('You cannot leave the org-wide thread');
    }
    await threadsApi.leave(threadId);
    // Do NOT set leftThreads here — let the `thread:you_left` socket event handle it
    // so the "X left the thread" system message arrives and is displayed first.
    const socket = getSocket();
    socket.emit('leave_thread', threadId);
  },

  loadUsers: async () => {
    const { data } = await usersApi.list();
    // @agent is a real users row so it can send/receive messages, but it's
    // not a teammate — exclude it from every picker/autocomplete that reads
    // this list (add/remove participant, task assignee, DM target, …).
    set({ users: data.filter((u) => !u.is_bot) });
  },

  resync: async () => {
    const socket = getSocket();
    const { activeThreadId } = get();

    // Reload the thread list so new chats and missed updates appear.
    try {
      const { data } = await threadsApi.list();
      set({ threads: data });
    } catch (err) {
      console.error('resync: failed to reload threads', err);
    }

    // Re-join the active thread room (room membership is lost on reconnect) and
    // refetch its messages to fill any gap from the disconnected window.
    if (activeThreadId) {
      socket.emit('join_thread', activeThreadId);
      try {
        const { data } = await messagesApi.list(activeThreadId);
        set((s) => ({ messages: { ...s.messages, [activeThreadId]: data } }));
      } catch (err) {
        console.error('resync: failed to reload messages', err);
      }
    }
  },

  setupSocketListeners: () => {
    const socket = getSocket();

    // On every (re)connect, re-join rooms and reconcile events missed while
    // disconnected. Socket.IO auto-reconnect reuses the same socket instance,
    // so this listener fires on each reconnection.
    socket.on('connect', () => {
      void get().resync();
    });

    socket.on('message:new', ({ threadId, message }: { threadId: string; message: Message }) => {
      // Don't deliver messages to threads the user has left
      if (get().leftThreads[threadId]) return;

      set((s) => {
        const existing = s.messages[threadId] ?? [];
        if (existing.find((m) => m.id === message.id)) return s;
        const newMessages = { ...s.messages, [threadId]: [...existing, message] };
        const threads = s.threads.map((t) =>
          t.id === threadId
            ? {
                ...t,
                last_message: message.content,
                last_message_at: message.created_at,
                last_message_sender: message.sender_name ?? '',
                last_message_is_internal: message.is_internal,
                unread_count: t.id === s.activeThreadId ? '0' : String((parseInt(t.unread_count ?? '0') || 0) + 1),
              }
            : t
        );
        return { messages: newMessages, threads };
      });
    });

    // @agent action-card state change (confirmed/executing/succeeded/failed/…).
    // The thread_messages row itself is never edited — this patches the
    // card's rendering in place by matching on pending_action.id, wherever
    // it happens to live across the currently-loaded threads.
    socket.on('action:updated', (patch: { pendingActionId: string } & Record<string, unknown>) => {
      set((s) => {
        const messages = { ...s.messages };
        for (const threadId of Object.keys(messages)) {
          const idx = messages[threadId].findIndex((m) => m.pending_action?.id === patch.pendingActionId);
          if (idx === -1) continue;
          const msgs = [...messages[threadId]];
          msgs[idx] = { ...msgs[idx], pending_action: { ...msgs[idx].pending_action!, ...patch, status: patch.status as PendingAction['status'] } };
          messages[threadId] = msgs;
          break;
        }
        return { messages };
      });
    });

    // A brand-new client thread was created (first inbound message from a new
    // contact). The server auto-joins thread rooms only at connect time, so an
    // already-connected user isn't in this new room yet. Reload the list so it
    // appears in the sidebar, and join its room so further messages arrive live.
    socket.on('thread:new', ({ threadId }: { threadId: string }) => {
      void (async () => {
        try {
          const { data } = await threadsApi.list();
          set({ threads: data });
        } catch (err) {
          console.error('thread:new: failed to reload threads', err);
        }
        getSocket().emit('join_thread', threadId);
      })();
    });

    socket.on('message:deleted', ({ threadId, messageId }: { threadId: string; messageId: string }) => {
      set((s) => ({
        messages: {
          ...s.messages,
          [threadId]: (s.messages[threadId] ?? []).filter((m) => m.id !== messageId),
        },
      }));
    });

    // User left a thread (echoed back to them by the server)
    socket.on('thread:you_left', ({ threadId }: { threadId: string }) => {
      set((s) => ({
        leftThreads: { ...s.leftThreads, [threadId]: {} },
      }));
      getSocket().emit('leave_thread', threadId);
    });

    // User was removed from a thread by someone else
    socket.on('thread:you_removed', ({ threadId, removedBy }: { threadId: string; removedBy: string }) => {
      set((s) => ({
        leftThreads: { ...s.leftThreads, [threadId]: { removedBy } },
      }));
      getSocket().emit('leave_thread', threadId);
    });

    // Thread metadata updated (participants, sender-identity override, and/or
    // the customer contact's name/company)
    socket.on('thread:updated', ({ threadId, participants, sender_identity, contact }: { threadId: string; participants?: unknown[]; sender_identity?: SenderIdentity; contact?: Thread['contact'] }) => {
      if (!participants && !sender_identity && !contact) return;
      set((s) => ({
        threads: s.threads.map((t) =>
          t.id === threadId
            ? {
                ...t,
                ...(participants ? { participants: participants as typeof t.participants } : {}),
                ...(sender_identity ? { sender_identity } : {}),
                ...(contact ? { contact } : {}),
              }
            : t
        ),
      }));
    });

    socket.on('presence:update', ({ userId, online }: { userId: string; online: boolean }) => {
      set((s) => {
        const next = new Set(s.onlineUserIds);
        if (online) next.add(userId); else next.delete(userId);
        return { onlineUserIds: next };
      });
    });

    socket.on('typing:start', ({ userId, name, threadId }: { userId: string; name: string; threadId: string }) => {
      set((s) => {
        const current = s.typingUsers[threadId] ?? [];
        if (current.find((u) => u.userId === userId)) return s;
        return { typingUsers: { ...s.typingUsers, [threadId]: [...current, { userId, name }] } };
      });
    });

    socket.on('typing:stop', ({ userId, threadId }: { userId: string; threadId: string }) => {
      set((s) => ({
        typingUsers: {
          ...s.typingUsers,
          [threadId]: (s.typingUsers[threadId] ?? []).filter((u) => u.userId !== userId),
        },
      }));
    });
  },
}));
